/**
 * BL-2318 — which lot fields a caller's role may edit at all (the "edit scope"),
 * shared by the lot PATCH route (authoritative) and the lot form (advisory), per
 * ADR 010 — Isomorphic Services and ADR 034 — Field Editability.
 *
 * Phase locks (sold / live bids) answer "can this field change right now";
 * the scope answers "may this role change it ever". Accounting edits a lot's
 * tax, load-out fee and title paperwork and nothing else (Dan Saper,
 * 2026-10-07); every other editing role edits the full set.
 */

/** `all` — every field the lot form edits; `accounting` — {@link ACCOUNTING_LOT_EDIT_FIELDS} only. */
export type LotEditScope = 'all' | 'accounting';

/**
 * The lot-form keys an `accounting`-scoped caller may change, in PATCH-body
 * (camelCase) spelling. `isTitledItem` is deliberately absent: it is catalog
 * content bidders see, and the title rows it reveals stay where the form
 * already shows them (BL-2318 AC 2, reworded by Dan Saper, 2026-10-08).
 */
export const ACCOUNTING_LOT_EDIT_FIELDS = [
  'taxStatus',
  'taxRateId',
  'hasLoadOutFee',
  'loadOutFee',
  'titleState',
  'titleStatus',
  'titleMailingStatus',
  'titleMailingDateSent',
  'titleMailingTrackingNumber',
  'titleMailingRecipientName',
  'titleMailingRecipientAddress',
] as const;

/** A key in {@link ACCOUNTING_LOT_EDIT_FIELDS}. */
export type AccountingLotEditField = (typeof ACCOUNTING_LOT_EDIT_FIELDS)[number];

/** The hint a control shows when the caller's role can't edit it (ADR 034's lock tier). */
export const ROLE_SCOPE_HINT = "Your role can't edit this field.";

/** The root error of a PATCH refused for carrying fields outside the caller's scope (403). */
export const ROLE_SCOPE_ERROR = "Your role can't change some of these fields.";

const ACCOUNTING_SET: ReadonlySet<string> = new Set(ACCOUNTING_LOT_EDIT_FIELDS);

/**
 * Resolve the caller's lot edit scope from what they hold.
 *
 * A super admin and anyone holding the full lot-edit grant (`inventory.update`)
 * edit everything; the accounting grant alone narrows to the accounting set;
 * holding neither means no edit at all. The full grant wins when both are held,
 * so an Admin (who holds both) is never narrowed.
 *
 * @param input.isSuperAdmin - The platform bypass.
 * @param input.holdsFullEdit - Holds `inventory.update`.
 * @param input.holdsAccountingEdit - Holds `lot.update_accounting`.
 * @returns The scope, or `null` when the caller may not edit a lot.
 */
export function resolveLotEditScope(input: {
  isSuperAdmin: boolean;
  holdsFullEdit: boolean;
  holdsAccountingEdit: boolean;
}): LotEditScope | null {
  if (input.isSuperAdmin || input.holdsFullEdit) return 'all';
  if (input.holdsAccountingEdit) return 'accounting';
  return null;
}

/**
 * Whether `field` is editable under `scope`.
 *
 * @param field - A lot-form / PATCH-body key.
 * @param scope - The caller's scope.
 * @returns `true` when the scope admits the field.
 */
export function isLotFieldInEditScope(field: string, scope: LotEditScope): boolean {
  return scope === 'all' || ACCOUNTING_SET.has(field);
}

/**
 * The keys of a request body the scope does not admit — the route refuses the
 * whole request when any are present, before writing anything.
 *
 * @param keys - The body's own keys.
 * @param scope - The caller's scope.
 * @returns The out-of-scope keys, in input order (empty when all are admitted).
 */
export function lotEditKeysOutsideScope(keys: readonly string[], scope: LotEditScope): string[] {
  return keys.filter((k) => !isLotFieldInEditScope(k, scope));
}

/**
 * Narrow a lot-edit payload to the keys the scope admits. The lot form sends
 * every field it holds, so an `accounting` save is narrowed before it is sent;
 * an `all` save is returned unchanged.
 *
 * @param body - The full payload.
 * @param scope - The caller's scope.
 * @returns A new object with only the admitted keys (or `body` itself for `all`).
 */
export function pickLotEditScope<T extends object>(body: T, scope: LotEditScope): Partial<T> {
  if (scope === 'all') return body;
  const picked: Partial<T> = {};
  for (const key of Object.keys(body) as Array<keyof T & string>) {
    if (ACCOUNTING_SET.has(key)) picked[key] = body[key];
  }
  return picked;
}
